﻿import { event } from '../';

/**
 * MediaQuery
 * Helper for managing media-query requirements for
 */
const MediaQuery = {
  /**
   * Breakpoints
   */
  breakpoints: {
    xs: {
      key: 'min-width',
      value: '375px',
    },
    sm: {
      key: 'min-width',
      value: '560px',
    },
    md: {
      key: 'min-width',
      value: '768px',
    },
    lg: {
      key: 'min-width',
      value: '992px',
    },
    xl: {
      key: 'min-width',
      value: '1200px',
    },
    xxl: {
      key: 'min-width',
      value: '1440px',
    },
    xxxl: {
      key: 'min-width',
      value: '1920px',
    },
  },
  /**
   * Orientational values
   */
  orientations: {
    portrait: {
      key: 'orientation',
      value: 'portrait',
    },
    landscape: {
      key: 'orientation',
      value: 'landscape',
    },
  },
  current: {},
  isInitialized: false,
  /**
   * Initialize current-state and append watcher for mq's changes
   */
  init() {
    if (this.isInitialized) {
      return;
    }

    this.isInitialized = true;
    this.current = this.getCurrent();

    const callback = () => {
      const currSize = this.getCurrent(),
        oldSize = this.current;

      if (currSize.breakpoint !== oldSize.breakpoint) {
        this.current = currSize;
        event.emit(window, 'change.mq', { currSize, oldSize });
      }
    };

    window.addEventListener('resize', callback);
  },
  /**
   * Check if current viewport is below a certain breakpoint
   * @param {String} name - name of breakpint
   * @returns {Boolean}
   */
  below(name = null) {
    if (name === null) return false;

    if (typeof this.breakpoints[name] === 'undefined') {
      throw new Error('Breakpoint (' + name + ') not found');
    }

    const query = '(max-width: ##TOKEN##)'.replace('##TOKEN##', this.breakpoints[name].value);
    return this._match(query);
  },
  /**
   * Check if current viewport is above a certain breakpint
   * @param {String} name - Name of breakpoint
   * @returns {Boolean}
   */
  above(name = null) {
    if (name === null) return false;

    if (typeof this.breakpoints[name] === 'undefined') {
      throw new Error('Breakpoint (' + name + ') not found');
    }

    const query = '(min-width: ##TOKEN##)'.replace('##TOKEN##', this.breakpoints[name].value);
    return this._match(query);
  },
  /**
   * Check if current viewport is between two breakpoints
   * @param {String} start - name of the smallest breakpoint
   * @param {String} end - name of the largest breakpoint
   * @returns {Boolean}
   */
  between(start = null, end = null) {
    if (start === null || end === null) {
      throw new Error('Breakpoints are either null or malformed');
    }

    if (typeof this.breakpoints[start] === 'undefined') {
      throw new Error('Breakpoint (start: ' + start + ') is not found');
    }

    if (typeof this.breakpoints[end] === 'undefined') {
      throw new Error('Breakpoint (end: ' + end + ') is not found');
    }

    const query =
      '(min-width: ' +
      this.breakpoints[start].value +
      ') AND (max-width: ' +
      this.breakpoints[end].value +
      ')';
    return this._match(query);
  },
  /**
   * Check if multiple breakpoint parameters matches name(s)
   * @param {String} name - Comma seperated string containing names of breakpoints or orientains
   * @param {String} requirement - Control which "direction" function is checking the breakpoint
   */
  is(name = null) {
    if (name === null) return false;

    let arrQueries = [],
      bps = this.breakpoints,
      strQuery = '(##KEY##: ##VALUE##)';

    // Check for combinations
    if (name.indexOf(',') > -1) {
      name.split(',').forEach((x) => {
        const key = x.trim(),
          bp = strQuery.replace('##KEY##', bps[key].key).replace('##VALUE##', bps[key].value);

        if (bp) arrQueries.push(bp);
      });
    } else {
      const bp = strQuery.replace('##KEY##', bps[name].key).replace('##VALUE##', bps[name].value);
      if (bp) arrQueries.push(bp);
    }

    arrQueries = arrQueries.join(' AND ');
    return this._match(arrQueries);
  },
  /**
   * Get current breakpoint object
   * @returns {Object}
   */
  getCurrent() {
    let bp = { breakpoint: 'xs', size: null };

    for (let point in this.breakpoints) {
      if (this.is(point)) {
        bp = { breakpoint: point, size: this.breakpoints[point].key };
      }
    }

    return bp;
  },
  /**
   * Get next breakpoint based on current breakpoint
   * @param {string} bp - Current breakpoint or queried breakpoint.
   */
  next(bp = String) {
    if (typeof bp === 'undefined' || bp === '') {
      return null;
    }

    const queryIndex = Object.keys(this.breakpoints).findIndex((q) => q.key === bp);

    /* eslint-disable-next-line */
    if (queryIndex === -1) {
    }

    const next = this.breakpoints[queryIndex + 1];
    return next ? next : null;
  },
  /**
   * Get next breakpoint based on current breakpoint
   * @param {string} bp - Current breakpoint or queried breakpoint.
   */
  prev(bp = String) {
    if (typeof bp === 'undefined' || bp === '') {
      return null;
    }

    const queryIndex = Object.keys(this.breakpoints).findIndex((q) => q === bp);

    /* eslint-disable-next-line */
    if (queryIndex === -1) {
    }

    const prev = this.breakpoints[queryIndex - 1];
    return prev ? prev : null;
  },
  /**
   * Match a query against matchMedia API
   * @type private
   * @param {string} query - Breakpoint query to test
   */
  _match(query) {
    return window.matchMedia(query).matches;
  },
};

export { MediaQuery };
